/* ==========================================================================
   Accessibility behavior
   --------------------------------------------------------------------------
   Custom JS added to address accessibility findings without editing the
   theme's compiled bundle (theme.aio.min.js) or third-party app scripts
   directly (Globo filter, Kaktus wishlist, etc).

   Loaded after theme.aio.min.js in layout/theme.liquid, deferred.
   ========================================================================== */

(function () {
  'use strict';

  document.addEventListener('DOMContentLoaded', function () {
    initPrimaryNavDropdowns();
    initWishlistButtons();
    initCartAnnouncer();
    initBackgroundVideoPause();
    initGloboFilterA11y();
    initFlickityDotsA11y();
  });

  /* ------------------------------------------------------------------------
     Flickity slide-nav dots (Press/Centennial hero sliders, and any other
     slideshow with dots enabled). Confirmed against the actual bundled
     Flickity source: dots are plain <li>s with a click handler only - no
     tabindex, no role. Flickity creates them dynamically after init, so we
     decorate via MutationObserver rather than a fixed Liquid anchor.
     ------------------------------------------------------------------------ */
  function initFlickityDotsA11y() {
    var pending = false;

    function decorate() {
      document.querySelectorAll('.flickity-page-dots').forEach(function (list) {
        var dots = list.querySelectorAll('li.dot');
        dots.forEach(function (dot, i) {
          if (!dot.hasAttribute('data-a11y-dot-bound')) {
            dot.setAttribute('data-a11y-dot-bound', 'true');
            dot.setAttribute('role', 'button');
            dot.setAttribute('tabindex', '0');
            dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
            dot.addEventListener('keydown', function (evt) {
              if (evt.key === 'Enter' || evt.key === ' ' || evt.keyCode === 13 || evt.keyCode === 32) {
                evt.preventDefault();
                dot.click();
              }
            });
          }
          // Flickity toggles is-selected on its own select/cellChange events -
          // syncing aria-current from that class on every decorate() pass
          // keeps it accurate regardless of whether the change came from a
          // dot click, an arrow, or autoplay advancing on its own.
          dot.setAttribute('aria-current', dot.classList.contains('is-selected') ? 'true' : 'false');
        });
      });
    }

    decorate();

    var observer = new MutationObserver(function () {
      if (pending) return;
      pending = true;
      requestAnimationFrame(function () {
        pending = false;
        decorate();
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  /* ------------------------------------------------------------------------
     Globo filter panel: "Skip to products" link + price slider focus
     restore. Globo injects its whole filter UI via its own JS - it's not
     server-rendered, so there's no Liquid anchor to hook. We wait for it to
     actually appear via MutationObserver rather than assuming a fixed
     load time.
     ------------------------------------------------------------------------ */
  function initGloboFilterA11y() {
    var skipLinkInserted = false;
    var productGrid = document.getElementById('ProductGrid');

    // Focus outline via a CSS rule (.noUi-handle:focus-visible in
    // accessibility.css) wasn't showing - Globo's own CSS is app-hosted, so
    // it's likely injected via JS at runtime, meaning it can load AFTER our
    // stylesheet and win a specificity tie on cascade order alone, or set
    // outline via inline style directly. Setting the outline here as an
    // inline style with 'important' priority - the strongest the CSS
    // cascade allows short of a user stylesheet - sidesteps that fight
    // entirely rather than continuing to guess at Globo's exact CSS.
    document.addEventListener('focusin', function (evt) {
      var handle = evt.target.closest && evt.target.closest('.noUi-handle');
      if (handle) {
        handle.style.setProperty('outline', '3px solid #005fcc', 'important');
        handle.style.setProperty('outline-offset', '2px', 'important');
      }
    });
    document.addEventListener('focusout', function (evt) {
      var handle = evt.target.closest && evt.target.closest('.noUi-handle');
      if (handle) {
        handle.style.removeProperty('outline');
        handle.style.removeProperty('outline-offset');
      }
    });

    function insertSkipLink(filterEl) {
      if (skipLinkInserted || !productGrid) return;
      var link = document.createElement('a');
      link.href = '#ProductGrid';
      link.className = 'in-page-link visually-hidden skip-link';
      link.textContent = (window.theme && theme.strings && theme.strings.skipToProducts) || 'Skip to products';
      filterEl.parentNode.insertBefore(link, filterEl);
      skipLinkInserted = true;
    }

    // Restores focus to a price-slider handle Globo just re-rendered (it
    // replaces the slider's markup on value change - e.g. via arrow keys -
    // dropping focus to <body>, confirmed live: an earlier version of this
    // fix used a fixed setTimeout after focusout, assuming the replacement
    // happened almost immediately. It doesn't - Globo's value-change re-
    // render involves an actual AJAX round-trip to refetch filtered
    // results, which reliably takes longer than any short fixed delay, so
    // the check fired before the real replacement and found nothing wrong.
    // Reacting to the actual DOM mutation (via the same observer that
    // handles the skip link, whenever it actually fires) instead of
    // guessing a delay works regardless of how long that round-trip takes.
    // Tracks which logical handle (lower/upper, via its own data-handle
    // attr) and which slider (via its own id) was focused, so we can find
    // the same one again by value rather than by a node reference that's
    // already gone.
    var lastHandleType = null;
    var lastSliderId = null;

    document.addEventListener('focusin', function (evt) {
      var handle = evt.target.closest && evt.target.closest('.noUi-handle');
      if (handle) {
        var slider = handle.closest('[id^="slider-"]');
        lastHandleType = handle.getAttribute('data-handle');
        lastSliderId = slider ? slider.id : null;
      } else if (evt.target !== document.body) {
        // Focus moved somewhere else on purpose - stop trying to restore.
        // document.body itself is excluded because that's the transient
        // state right after Globo removes the focused handle, before this
        // same observer has had a chance to restore it - clearing here
        // would abandon the restore attempt before it can run.
        lastHandleType = null;
        lastSliderId = null;
      }
    });

    function restoreSliderFocus() {
      if (!lastSliderId || document.activeElement !== document.body) return;
      var slider = document.getElementById(lastSliderId);
      var handleEl = slider && slider.querySelector('.noUi-handle[data-handle="' + lastHandleType + '"]');
      if (handleEl) handleEl.focus();
    }

    var observer = new MutationObserver(function () {
      if (!skipLinkInserted) {
        var filterTrigger = document.querySelector('.gf-filter-header, .gf-filter-trigger');
        if (filterTrigger) insertSkipLink(filterTrigger);
      }
      restoreSliderFocus();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  /* ------------------------------------------------------------------------
     Background/hero video pause control (WCAG 2.2.2).
     video-div.liquid renders <video loop muted playsinline autoplay> with
     no controls at all - no way to pause. Verified this section only ever
     auto-plays once (theme.initWhenVisible, one-shot), never auto-pauses on
     scroll, so a simple toggle button here won't fight any existing logic.
     Liquid provides data-a11y-label (the section's own title/heading text,
     or a generic fallback) on each video tag - we build the button and its
     accessible name from that here rather than duplicating button markup
     across every section that can render one of these videos.
     ------------------------------------------------------------------------ */
  function initBackgroundVideoPause() {
    var videos = document.querySelectorAll('video.video-div[data-type="mp4"]');
    if (!videos.length) return;

    var pauseIcon = '<svg aria-hidden="true" focusable="false" viewBox="0 0 10 13"><path fill="currentColor" d="M0 0h3v13H0zM7 0h3v13H7z"/></svg>';
    var playIcon = '<svg aria-hidden="true" focusable="false" viewBox="18.24 17.35 24.52 28.3"><path fill="currentColor" d="M22.1 19.151v25.5l20.4-13.489-20.4-12.011z"/></svg>';

    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    videos.forEach(function (video) {
      var label = video.getAttribute('data-a11y-label') || 'Background video';
      video.setAttribute('aria-label', label);
      // Hints it's clickable, since there's no visible button for
      // mouse/touch users - they toggle via the video itself.
      video.style.cursor = 'pointer';

      // Liquid can't know the visitor's reduced-motion preference at render
      // time, so the video still autoplays briefly before this JS runs -
      // pause it immediately and let the control offer to start it instead.
      if (reduceMotion) {
        video.pause();
      }

      function toggle() {
        if (video.paused) {
          video.play();
        } else {
          video.pause();
        }
      }

      // Mouse/touch users: click anywhere on the video itself. Clicks on
      // anything stacked visually above it (e.g. a hero CTA link/button)
      // still reach that element instead, per normal hit-testing - no
      // conflict since this listener is on the <video> element itself, not
      // a full-bleed overlay behind it.
      video.addEventListener('click', toggle);

      // Keyboard-only control: visually hidden until it receives focus (see
      // .a11y-video-pause in accessibility.css) - a real, discoverable
      // button for keyboard users without a permanent icon cluttering the
      // hero for everyone else.
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'a11y-video-pause';
      video.parentNode.insertBefore(btn, video.nextSibling);
      btn.addEventListener('click', toggle);

      function updateButton() {
        var isPaused = video.paused;
        btn.setAttribute('aria-pressed', isPaused ? 'false' : 'true');
        btn.setAttribute('aria-label', (isPaused ? 'Play ' : 'Pause ') + label);
        btn.innerHTML = isPaused ? playIcon : pauseIcon;
      }

      video.addEventListener('play', updateButton);
      video.addEventListener('pause', updateButton);
      updateButton();
    });
  }

  /* ------------------------------------------------------------------------
     Add-to-cart screen-reader announcement.
     The theme's AJAX cart flow (drawer/no-reload add-to-cart) gives sighted
     users a small count-bubble update but no non-visual confirmation at
     all. Impulse already dispatches ajaxProduct:added / ajaxProduct:error
     (confirmed present in the served theme.aio.min.js, not just the
     readable source) - we hook those rather than patching fetch/XHR
     ourselves. Only the AJAX path needs this: a full-page-reload add-to-cart
     template's reload is its own confirmation.
     ------------------------------------------------------------------------ */
  function initCartAnnouncer() {
    var statusRegion = document.getElementById('A11yCartStatus');
    var alertRegion = document.getElementById('A11yCartAlert');
    if (!statusRegion || !alertRegion) return;

    function speak(region, message) {
      // Clear first, then set on a short delay - some screen readers won't
      // re-announce a live region if the text content doesn't actually
      // change, which matters if someone adds the same product twice.
      region.textContent = '';
      window.setTimeout(function () {
        region.textContent = message;
      }, 50);
    }

    function cartUrl() {
      return (window.theme && theme.routes && theme.routes.cart) || '/cart.js';
    }

    document.addEventListener('ajaxProduct:added', function (evt) {
      var product = evt.detail && evt.detail.product;
      var title = product && product.title;

      fetch(cartUrl(), { headers: { Accept: 'application/json' } })
        .then(function (res) { return res.json(); })
        .then(function (cart) {
          var count = cart.item_count;
          var itemWord = count === 1 ? 'item' : 'items';
          var message = title
            ? 'Added ' + title + ' to cart. ' + count + ' ' + itemWord + ' in cart.'
            : 'Added to cart. ' + count + ' ' + itemWord + ' in cart.';
          speak(statusRegion, message);
        })
        .catch(function () {
          speak(statusRegion, title ? 'Added ' + title + ' to cart.' : 'Added to cart.');
        });
    });

    document.addEventListener('ajaxProduct:error', function () {
      speak(alertRegion, 'There was a problem adding this item to your cart.');
    });
  }

  /* ------------------------------------------------------------------------
     Kaktus Wishlist app buttons are plain <div>s (no role, no tabindex, no
     accessible name) - not focusable, no keyboard path. We can't edit the
     app's own markup and it re-renders these nodes itself on state changes
     (add/remove, count updates), so we decorate on load and re-decorate
     whenever the app mutates the DOM, rather than trying to replace its
     elements with real <button>s (which the app would just overwrite).
     Covers both the PDP "SAVE TO WISHLIST" control and the site-wide
     "MY WISHLIST" viewer trigger - both share the .kaktus-wishlist-button
     class.
     ------------------------------------------------------------------------ */
  function initWishlistButtons() {
    // NOTE: deliberately no "is there a button yet?" early return here. An
    // earlier version bailed out if .kaktus-wishlist-button wasn't already
    // in the DOM at DOMContentLoaded - but Kaktus injects its widget later,
    // from a remote async script, so that guard always fired first and
    // returned before setting up the very observer meant to catch that
    // injection. The observer has to be registered unconditionally.

    function decorate(root) {
      var els = root.querySelectorAll('.kaktus-wishlist-button');
      els.forEach(function (el) {
        el.setAttribute('role', 'button');
        el.setAttribute('tabindex', '0');

        var svg = el.querySelector('svg');
        if (svg) svg.setAttribute('aria-hidden', 'true');

        // Toggle state comes from Kaktus's own kaktus-w-completed class on
        // the host element (their stylesheet keys the "saved" background
        // colour off it). Not from the button text: their markup sets
        // data-text and data-text-completed to the SAME string ("Save to
        // Wishlist"), so the label never changes between states.
        el.setAttribute('aria-pressed', el.classList.contains('kaktus-w-completed') ? 'true' : 'false');

        if (!el.hasAttribute('data-a11y-wishlist-bound')) {
          el.setAttribute('data-a11y-wishlist-bound', 'true');
          el.addEventListener('keydown', function (evt) {
            if (evt.key === 'Enter' || evt.key === ' ' || evt.keyCode === 13 || evt.keyCode === 32) {
              evt.preventDefault();
              el.click();
            }
          });
        }
      });
    }

    decorate(document);

    var pending = false;
    var observer = new MutationObserver(function () {
      if (pending) return;
      pending = true;
      requestAnimationFrame(function () {
        pending = false;
        decorate(document);
      });
    });
    // attributes/attributeFilter matter as much as childList here: Kaktus
    // signals the saved state by toggling the kaktus-w-completed class on
    // the existing element rather than replacing it, which is an attribute
    // mutation - a childList-only observer would leave aria-pressed stale
    // after the user adds or removes an item.
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });
  }

  /* ------------------------------------------------------------------------
     Primary nav keyboard fix.
     Top-level items with a dropdown now render as <button> elements (see
     snippets/header-desktop-nav.liquid). A real <button> already fires a
     'click' event natively for both Enter and Space, so no separate keydown
     handling is needed for activation - only for Escape.
     ------------------------------------------------------------------------ */
  function initPrimaryNavDropdowns() {
    var triggers = document.querySelectorAll('.site-nav--has-dropdown > .site-nav__link--has-dropdown');
    if (!triggers.length) return;

    // theme.aio.min.js (compiled bundle, can't be edited) still binds its own
    // touchend handler directly on elements matching .site-nav__link--has-dropdown,
    // assuming a real href to fall back to on a second tap - our button has
    // none, which would break navigation on touch devices at desktop-nav
    // width (iPad landscape, touchscreen laptops). A listener on the button
    // itself would fire in registration order (theirs loaded first, so
    // theirs would still win even with capture:true here) - intercepting on
    // an ancestor during the capture phase runs before the event ever
    // reaches the button, regardless of load order, so this reliably wins.
    // stopPropagation (not preventDefault) only blocks their JS handler;
    // the browser's native touch->click synthesis still proceeds normally,
    // so our own click listener below still receives it.
    document.addEventListener('touchend', function (evt) {
      var trigger = evt.target.closest && evt.target.closest('.site-nav__link--has-dropdown');
      if (trigger && trigger.closest('.site-nav--has-dropdown')) {
        evt.stopPropagation();
      }
    }, { capture: true });

    // Tab reachability is managed here, independent of visibility - see the
    // comment above .a11y-nav-open in accessibility.css for why. Every
    // dropdown link is tabindex="-1" unless the panel was deliberately
    // opened via this button, regardless of whether the theme's own CSS
    // happens to be showing it (hover, or mere Tab-focus on the trigger).
    function setLinksTabbable(li, tabbable) {
      li.querySelectorAll('.site-nav__dropdown a').forEach(function (link) {
        if (tabbable) {
          link.removeAttribute('tabindex');
        } else {
          link.setAttribute('tabindex', '-1');
        }
      });
    }

    function closeItem(li, btn) {
      li.classList.remove('a11y-nav-open');
      btn.setAttribute('aria-expanded', 'false');
      setLinksTabbable(li, false);
    }

    function openItem(li, btn) {
      li.classList.add('a11y-nav-open');
      btn.setAttribute('aria-expanded', 'true');
      setLinksTabbable(li, true);
    }

    function closeAll(exceptLi) {
      triggers.forEach(function (btn) {
        var li = btn.closest('.site-nav--has-dropdown');
        if (li && li !== exceptLi && li.classList.contains('a11y-nav-open')) {
          closeItem(li, btn);
        }
      });
    }

    triggers.forEach(function (btn) {
      var li = btn.closest('.site-nav--has-dropdown');
      if (!li) return;

      // Start closed - links excluded from tab order until deliberately
      // opened, regardless of whether the theme's own CSS already shows
      // the panel (e.g. the mouse happens to be resting over it on load).
      setLinksTabbable(li, false);

      btn.addEventListener('click', function () {
        var wasOpen = li.classList.contains('a11y-nav-open');
        closeAll(li);
        if (wasOpen) {
          closeItem(li, btn);
        } else {
          openItem(li, btn);
        }
      });

      li.addEventListener('keydown', function (evt) {
        if (evt.key === 'Escape' || evt.keyCode === 27) {
          if (li.classList.contains('a11y-nav-open')) {
            evt.stopPropagation();
            closeItem(li, btn);
            btn.focus();
          }
        }
      });
    });

    // Clicking anywhere outside a nav item closes any open dropdown.
    document.addEventListener('click', function (evt) {
      if (!evt.target.closest('.site-nav--has-dropdown')) {
        closeAll(null);
      }
    });

    // Tabbing focus away from an item's own subtree closes it, so it doesn't
    // stay open after the user has moved on.
    document.addEventListener('focusin', function (evt) {
      triggers.forEach(function (btn) {
        var li = btn.closest('.site-nav--has-dropdown');
        if (!li || li.contains(evt.target)) return;

        if (li.classList.contains('a11y-nav-open')) {
          closeItem(li, btn);
        }

        // Also clear the theme's own .is-focused. Its compiled bundle adds
        // that class on focusin but only ever removes it on mouseleave -
        // there's no focusout handler in it - so tabbing from the last
        // dropdown trigger straight out of the nav (e.g. to the search box)
        // left the panel visually stuck open. Moving between nav items
        // hides this, because the theme's own accessibleMouseEvent calls
        // closeDropdowns() on the next trigger; only leaving the nav
        // entirely exposes it.
        // The :hover guard matters - .is-focused is the same class the
        // theme uses for mouse hover, so without it, tabbing elsewhere
        // would rip away a menu the user is actively hovering.
        if (li.classList.contains('is-focused') && !li.matches(':hover')) {
          li.classList.remove('is-focused');
        }
      });
    });
  }
})();
